import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import axios from "axios";
import socket from "../../../../../socket-io.js";
import { serverHost } from "../../../../../http_common.js";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import ChatBubbleOutlineRoundedIcon from "@mui/icons-material/ChatBubbleOutlineRounded";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import DoneAllRoundedIcon from "@mui/icons-material/DoneAllRounded";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import "./uniCloudChat.css";

const initials = name => (name || "?").trim().split(/\s+/).slice(0, 2).map(part => part[0] || "").join("").toUpperCase();
const validDate = value => {
  const date = value ? new Date(value) : null;
  return date && !Number.isNaN(date.getTime()) ? date : null;
};
const messageTime = value => validDate(value)?.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }) || "";
const listTime = value => {
  const date = validDate(value);
  if (!date) return "";
  return date.toDateString() === new Date().toDateString()
    ? messageTime(value)
    : date.toLocaleDateString([], { month: "short", day: "numeric" });
};
const dayLabel = value => {
  const date = validDate(value);
  if (!date) return "Recent messages";
  const now = new Date();
  if (date.toDateString() === now.toDateString()) return "Today";
  const yesterday = new Date(now); yesterday.setDate(yesterday.getDate() - 1);
  if (date.toDateString() === yesterday.toDateString()) return "Yesterday";
  return date.toLocaleDateString([], { month: "long", day: "numeric", year: "numeric" });
};
const sameDay = (one, other) => {
  const a = validDate(one); const b = validDate(other);
  return !!a && !!b && a.toDateString() === b.toDateString();
};
const sameGroup = (one, other) => {
  const a = validDate(one?.createdAt); const b = validDate(other?.createdAt);
  return !!one && !!other && one.senderId === other.senderId && a && b &&
    Math.abs(b.getTime() - a.getTime()) < 5 * 60 * 1000 && sameDay(a, b);
};
const messageKey = message => String(message?._id || message?.clientMessageId || "");
// Display an image attachment only if a future authorized server supplies one.
// The current backend accepts text-only messages; this does not enable uploads.
const imageSource = message => {
  const candidate = message?.imageUrl || message?.attachment?.url;
  return typeof candidate === "string" && /^https:\/\//i.test(candidate) ? candidate : null;
};
const combineMessages = (existing, incoming) => {
  const seen = new Set();
  return [...existing, ...incoming].filter(message => {
    const key = messageKey(message);
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  }).sort((a, b) => (validDate(a.createdAt)?.getTime() || 0) - (validDate(b.createdAt)?.getTime() || 0));
};

function PersonAvatar({ person, small = false }) {
  // Only render a full URL. Invalid or missing profile pictures fall back to initials.
  const photo = typeof person?.photo === "string" && /^https?:\/\//i.test(person.photo) ? person.photo : "";
  const [imageFailed, setImageFailed] = useState(false);
  useEffect(() => setImageFailed(false), [photo]);
  return <span className={`uc-avatar${small ? " uc-avatar-small" : ""}`} aria-hidden="true">
    {photo && !imageFailed ? <img src={photo} alt="" onError={() => setImageFailed(true)} /> : initials(person?.name)}
  </span>;
}

// Keep the company's existing private-chat contract. This component never chooses a
// company ID, and cannot initiate group, cross-company, or file-upload requests.
export default function UniCloudChat({ onClose }) {
  const { accessType } = useSelector(state => state.clientFunction);
  const company = useSelector(state => state.clientFunction?.companiesDB?.clientData);
  const staff = useSelector(state => state.clientFunction?.companiesDB?.staffData);
  const myId = accessType === "USER_ACCESS" ? staff?.userId : company?.founder?.founderId || company?.clientId;
  const [participants, setParticipants] = useState([]);
  const [selected, setSelected] = useState(null);
  const [messages, setMessages] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [search, setSearch] = useState("");
  const [mobileConversation, setMobileConversation] = useState(false);
  const [showDetails, setShowDetails] = useState(true);
  const [detailsTab, setDetailsTab] = useState("about");
  const [loadingPeople, setLoadingPeople] = useState(true);
  const [loading, setLoading] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [hasOlder, setHasOlder] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [closing, setClosing] = useState(false);
  const selectedRef = useRef(null);
  const messagesRef = useRef(null);
  const shouldScrollRef = useRef(true);
  const isMountedRef = useRef(true);
  const closeTimer = useRef(null);
  const closingRef = useRef(false);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const selectedId = selected?.id;
  const text = drafts[selectedId] || "";
  selectedRef.current = selectedId;

  const closeChat = useCallback(() => {
    if (closingRef.current) return;
    closingRef.current = true;
    setClosing(true);
    closeTimer.current = window.setTimeout(() => onCloseRef.current?.(), 200);
  }, []);
  useEffect(() => {
    const keyHandler = event => { if (event.key === "Escape") { event.stopPropagation(); closeChat(); } };
    document.addEventListener("keydown", keyHandler);
    return () => { document.removeEventListener("keydown", keyHandler); window.clearTimeout(closeTimer.current); };
  }, [closeChat]);
  useEffect(() => {
    isMountedRef.current = true;
    return () => { isMountedRef.current = false; };
  }, []);

  const refreshPeople = useCallback(async () => {
    try {
      const { data } = await axios.get(`${serverHost}/unicloud/chat/participants`);
      if (data?.status !== 200) throw new Error(data?.message || "Unable to load teammates.");
      if (isMountedRef.current) setParticipants(Array.isArray(data.participants) ? data.participants : []);
    } catch (err) {
      if (isMountedRef.current) setError(err.response?.data?.message || err.message || "Unable to load teammates.");
    } finally {
      if (isMountedRef.current) setLoadingPeople(false);
    }
  }, []);
  useEffect(() => { refreshPeople(); }, [refreshPeople]);

  const markRead = useCallback(id => {
    if (!socket.connected || !id) return;
    socket.emit("chat:read", { recipientId: id }, ack => {
      if (ack?.status === 200 && isMountedRef.current) {
        setParticipants(previous => previous.map(person => person.id === id ? { ...person, unreadCount: 0 } : person));
      }
    });
  }, []);
  useEffect(() => {
    const onIncoming = message => {
      if (!message?.senderId || !message?.recipientId) return;
      const otherId = message.senderId === myId ? message.recipientId : message.senderId;
      if (otherId === selectedRef.current) {
        const pane = messagesRef.current;
        shouldScrollRef.current = !pane || pane.scrollHeight - pane.scrollTop - pane.clientHeight < 110;
        setMessages(previous => combineMessages(previous, [message]));
        if (message.recipientId === myId) markRead(otherId);
      }
      refreshPeople();
    };
    const onRead = payload => {
      if (payload?.readerId && payload.readerId === selectedRef.current) {
        setMessages(previous => previous.map(message => message.senderId === myId && !message.readAt ? { ...message, readAt: new Date().toISOString() } : message));
      }
      refreshPeople();
    };
    socket.on("chat:message", onIncoming);
    socket.on("chat:read", onRead);
    return () => { socket.off("chat:message", onIncoming); socket.off("chat:read", onRead); };
  }, [myId, markRead, refreshPeople]);
  useEffect(() => {
    if (shouldScrollRef.current && messagesRef.current) {
      messagesRef.current.scrollTop = messagesRef.current.scrollHeight;
    }
  }, [messages]);

  useEffect(() => {
    if (!selectedId) return;
    let cancelled = false;
    setLoading(true);
    setMessages([]);
    setError("");
    setHasOlder(false);
    shouldScrollRef.current = true;
    axios.get(`${serverHost}/unicloud/chat/messages/${encodeURIComponent(selectedId)}`)
      .then(({ data }) => {
        if (cancelled || selectedRef.current !== selectedId) return;
        if (data?.status !== 200) throw new Error(data?.message || "Unable to open conversation.");
        const page = Array.isArray(data.messages) ? data.messages : [];
        setMessages(combineMessages([], page));
        setHasOlder(page.length === 50);
        markRead(selectedId);
      })
      .catch(err => { if (!cancelled) setError(err.response?.data?.message || err.message || "Unable to open conversation."); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [selectedId, markRead]);

  const loadOlder = async () => {
    if (!selectedId || !hasOlder || loadingOlder || !messages.length) return;
    const firstDate = messages[0]?.createdAt;
    if (!firstDate) return;
    setLoadingOlder(true);
    const pane = messagesRef.current;
    const heightBefore = pane?.scrollHeight || 0;
    const topBefore = pane?.scrollTop || 0;
    shouldScrollRef.current = false;
    try {
      const { data } = await axios.get(`${serverHost}/unicloud/chat/messages/${encodeURIComponent(selectedId)}`, { params: { before: firstDate } });
      if (data?.status !== 200) throw new Error(data?.message || "Unable to load earlier messages.");
      if (selectedRef.current !== selectedId) return;
      const older = Array.isArray(data.messages) ? data.messages : [];
      setMessages(previous => combineMessages(previous, older));
      setHasOlder(older.length === 50);
      window.requestAnimationFrame(() => {
        if (messagesRef.current && selectedRef.current === selectedId) {
          messagesRef.current.scrollTop = topBefore + messagesRef.current.scrollHeight - heightBefore;
        }
      });
    } catch (err) {
      if (selectedRef.current === selectedId) setError(err.response?.data?.message || err.message || "Unable to load earlier messages.");
    } finally {
      if (isMountedRef.current) setLoadingOlder(false);
    }
  };

  const updateDraft = value => setDrafts(previous => ({ ...previous, [selectedId]: value }));
  const send = event => {
    event.preventDefault();
    const content = text.trim();
    const recipientId = selectedId;
    if (!recipientId || !content || sending) return;
    if (!socket.connected) { setError("Chat is disconnected. Reconnect and try again."); return; }
    setSending(true);
    setError("");
    const clientMessageId = window.crypto?.randomUUID?.() || `msg-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    socket.timeout(12000).emit("chat:send", { recipientId, text: content, clientMessageId }, (err, ack) => {
      if (!isMountedRef.current) return;
      setSending(false);
      if (err || ack?.status !== 200 || !ack?.message) {
        setError(ack?.message || "Message confirmation timed out. Check the conversation before retrying.");
        return;
      }
      if (selectedRef.current === recipientId) {
        shouldScrollRef.current = true;
        setMessages(previous => combineMessages(previous, [ack.message]));
      }
      setDrafts(previous => ({ ...previous, [recipientId]: previous[recipientId] === text ? "" : previous[recipientId] }));
      refreshPeople();
    });
  };

  const displayedPeople = useMemo(() => participants
    .filter(person => `${person.name || ""} ${person.role || ""}`.toLowerCase().includes(search.trim().toLowerCase()))
    .sort((a, b) => (validDate(b.lastMessage?.createdAt)?.getTime() || 0) - (validDate(a.lastMessage?.createdAt)?.getTime() || 0)), [participants, search]);
  const unreadTotal = participants.reduce((total, person) => total + (Number(person.unreadCount) || 0), 0);
  const selectedPerson = participants.find(person => person.id === selectedId) || selected;

  return <section className={`uc-chat${mobileConversation ? " uc-mobile-open" : ""}${showDetails ? " uc-details-open" : ""}${closing ? " uc-chat-exiting" : ""}`} role="dialog" aria-modal="true" aria-label="UniCloud team communication" onClick={event => event.stopPropagation()}>
    <aside className="uc-people" aria-label="Conversations">
      <div className="uc-people-top">
        <div className="uc-main-heading"><div className="uc-logo"><ChatBubbleOutlineRoundedIcon /></div><div><span className="uc-eyebrow">UNICLOUD</span><h2>Chat</h2></div></div>
        <span className="uc-head-count" title="Authorized teammates">{participants.length} members</span>
      </div>
      <div className="uc-search-wrap"><SearchRoundedIcon fontSize="small" /><input type="search" aria-label="Find a teammate" placeholder="Search conversations" value={search} onChange={event => setSearch(event.target.value)} /></div>
      <div className="uc-list-label"><span>Recent conversations</span>{unreadTotal > 0 && <strong>{unreadTotal} unread</strong>}</div>
      <div className="uc-people-list">
        {loadingPeople ? <p className="uc-list-feedback">Loading your team…</p> : displayedPeople.map(person =>
          <button type="button" key={person.id} className={`uc-person${selectedId === person.id ? " uc-person-selected" : ""}`} onClick={() => { setSelected(person); setMobileConversation(true); setDetailsTab("about"); }}>
            <PersonAvatar person={person} />
            <span className="uc-person-copy"><span className="uc-person-topline"><strong>{person.name || "Team member"}</strong><time>{listTime(person.lastMessage?.createdAt)}</time></span><span className="uc-person-role">{person.role || (person.type === "CLIENT_ACCESS" ? "Company founder" : "Team member")}</span><span className="uc-person-last">{person.lastMessage?.senderId === myId ? "You: " : ""}{person.lastMessage?.text || "Start a conversation"}</span></span>
            {Number(person.unreadCount) > 0 && <b className="uc-unread">{person.unreadCount}</b>}
          </button>
        )}
        {!loadingPeople && participants.length === 0 && <div className="uc-list-feedback">No teammates are available for messaging yet.</div>}
        {!loadingPeople && participants.length > 0 && displayedPeople.length === 0 && <div className="uc-list-feedback">No conversations match your search.</div>}
      </div>
      <div className="uc-sidebar-foot"><LockOutlinedIcon fontSize="small" /> Private to your company</div>
    </aside>

    <main className="uc-conversation" aria-label="Messages">
      <header className="uc-conversation-head">
        {selectedPerson ? <>
          <button className="uc-back" type="button" aria-label="Back to teammates" onClick={() => setMobileConversation(false)}><ArrowBackRoundedIcon /></button>
          <PersonAvatar person={selectedPerson} small />
          <div className="uc-conversation-title"><strong>{selectedPerson.name}</strong><span>{selectedPerson.role || "Company teammate"} · Direct conversation</span></div>
          <button type="button" className="uc-details-toggle" aria-label={showDetails ? "Hide conversation details" : "Show conversation details"} aria-pressed={showDetails} onClick={() => setShowDetails(value => !value)}><InfoOutlinedIcon /></button>
        </> : <><span className="uc-conversation-title"><strong>Messages</strong><span>Choose a teammate to start</span></span></>}
        <button type="button" className="uc-close" aria-label="Close UniCloud chat" onClick={closeChat}><CloseOutlinedIcon /></button>
      </header>
      {selectedPerson ? <>
        <div className="uc-messages" ref={messagesRef} aria-live="polite" aria-relevant="additions text">
          {hasOlder && <button className="uc-load-older" type="button" onClick={loadOlder} disabled={loadingOlder}>{loadingOlder ? "Loading…" : "Load earlier messages"}</button>}
          {loading ? <p className="uc-messages-notice">Opening conversation…</p> : messages.length === 0 ? <div className="uc-empty-chat"><span className="uc-empty-symbol"><ChatBubbleOutlineRoundedIcon /></span><h3>Start a conversation</h3><p>Your secure conversation with {selectedPerson.name} starts here.</p></div> : messages.map((message, index) => {
            const previous = messages[index - 1];
            const following = messages[index + 1];
            const mine = message.senderId === myId;
            const groupStart = !sameGroup(previous, message);
            const groupEnd = !sameGroup(message, following);
            const newDay = !previous || !sameDay(previous.createdAt, message.createdAt);
            return <div key={messageKey(message) || `row-${index}`}>
              {newDay && <div className="uc-day"><span>{dayLabel(message.createdAt)}</span></div>}
              <div className={`uc-message-row${mine ? " uc-outgoing" : " uc-incoming"}${groupEnd ? " uc-last" : ""}${groupStart ? " uc-first" : ""}`}>
                {!mine && <span className="uc-message-avatar">{groupEnd ? <PersonAvatar person={selectedPerson} small /> : null}</span>}
                <div className="uc-message-stack">
                  <div className={`uc-bubble${imageSource(message) ? " uc-bubble-with-image" : ""}`}>
                    {imageSource(message) && <img className="uc-message-image" src={imageSource(message)} alt="Shared chat attachment" loading="lazy" />}
                    {message.text && <p>{message.text}</p>}
                  </div>
                  {groupEnd && <div className="uc-message-meta"><time dateTime={message.createdAt || undefined}>{messageTime(message.createdAt)}</time>{mine && message.readAt && <DoneAllRoundedIcon className="uc-read-icon" titleAccess="Read" fontSize="inherit" />}</div>}
                </div>
              </div>
            </div>;
          })}
        </div>
        <form className="uc-compose" onSubmit={send}>
          <label className="uc-input-wrap"><span className="uc-sr-only">Message to {selectedPerson.name}</span><textarea aria-label={`Message to ${selectedPerson.name}`} value={text} onChange={event => updateDraft(event.target.value)} placeholder="Write your message…" rows={1} maxLength={4000} onKeyDown={event => { if (event.key === "Enter" && !event.shiftKey && !event.nativeEvent.isComposing) { event.preventDefault(); send(event); } }} /></label>
          <button className="uc-send" type="submit" disabled={sending || !text.trim()} aria-label={sending ? "Sending message" : "Send message"}><SendRoundedIcon /> <span>{sending ? "Sending" : "Send"}</span></button>
        </form>
      </> : <div className="uc-welcome"><div className="uc-welcome-icon"><GroupsOutlinedIcon /></div><h3>Stay close to your team</h3><p>Select a colleague on the left to continue an existing conversation or start a new one.</p><span><LockOutlinedIcon fontSize="small" /> Your messages stay within your company</span></div>}
      {error && <div className="uc-error" role="alert"><span>{error}</span><button type="button" aria-label="Dismiss notification" onClick={() => setError("")}>×</button><button type="button" onClick={() => { setError(""); refreshPeople(); }} aria-label="Retry loading teammates"><RefreshRoundedIcon fontSize="small" /></button></div>}
    </main>

    <aside className="uc-details" aria-label="Conversation details">
      <div className="uc-details-head"><strong>Conversation details</strong><button type="button" aria-label="Hide conversation details" onClick={() => setShowDetails(false)}><CloseOutlinedIcon fontSize="small" /></button></div>
      {selectedPerson ? <>
        <div className="uc-person-profile"><PersonAvatar person={selectedPerson} /><h3>{selectedPerson.name}</h3><span>{selectedPerson.role || (selectedPerson.type === "CLIENT_ACCESS" ? "Company founder" : "Team member")}</span><p>Internal company conversation</p></div>
        <div className="uc-detail-tabs" role="tablist" aria-label="Conversation information"><button type="button" role="tab" aria-selected={detailsTab === "about"} className={detailsTab === "about" ? "active" : ""} onClick={() => setDetailsTab("about")}>About</button><button type="button" role="tab" aria-selected={detailsTab === "files"} className={detailsTab === "files" ? "active" : ""} onClick={() => setDetailsTab("files")}>Shared files</button></div>
        {detailsTab === "about" ? <div className="uc-detail-content"><div className="uc-detail-row"><GroupsOutlinedIcon /><span><strong>Company teammate</strong><small>Authorized messaging participant</small></span></div><div className="uc-detail-row"><LockOutlinedIcon /><span><strong>Private conversation</strong><small>Only company-authorized users may exchange messages</small></span></div><div className="uc-detail-row"><ChatBubbleOutlineRoundedIcon /><span><strong>{messages.length} loaded messages</strong><small>Older messages can be loaded from the conversation</small></span></div></div> : <div className="uc-files-empty"><div><FolderOutlinedIcon /></div><strong>No shared files available</strong><p>File and image attachments are not enabled by the current UniCloud chat service.</p><span><InsertDriveFileOutlinedIcon fontSize="small" /> Text messaging is available now</span></div>}
      </> : <div className="uc-details-placeholder"><InfoOutlinedIcon /><p>Select a teammate to view conversation information.</p></div>}
    </aside>
  </section>;
}
